/*
 * End of Summer Sale countdown (25 to 30 September 2026).
 *
 * All three placements (slideshow banner, announcement bar, product page) render
 * the same <sale-countdown> element with the same data attributes, so they are
 * synchronised by construction: the target is derived from the same absolute
 * epoch values, never from the visitor's own clock.
 *
 * Daily reset: the campaign runs from data-start to data-end. With
 * data-daily-reset the timer counts down to the next boundary at
 * start + n * data-cycle (24 hours) and then starts over. The final cycle is
 * clamped to data-end, after which the element disappears.
 *
 * Time zone: start and end arrive as epoch milliseconds from Liquid, derived
 * from an ISO string with an explicit offset (+01:00). Every visitor therefore
 * sees the same reading, wherever they are.
 *
 * Labels come from data-labels so this file carries no language of its own and
 * is identical across stores.
 *
 * After the campaign: switch off "Show sale countdown" in theme settings. This
 * asset and the related blocks can then be removed.
 */
(function () {
  var FALLBACK_LABELS = {
    day: 'day', days: 'days', hour: 'hour', hours: 'hours',
    minute: 'min', minutes: 'min', second: 'sec', seconds: 'sec',
    d: 'd', h: 'h', m: 'm', s: 's'
  };

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /*
   * QA aid: with ?sale_countdown_preview in the URL the whole window shifts to
   * now, so the timer can be reviewed before the campaign starts. Duration and
   * daily cycle stay the same. Resolved once per page so every timer gets the
   * same shift and they stay in step. Without the parameter the shift is 0.
   */
  var previewShiftMs = null;
  function previewShift(start) {
    if (previewShiftMs !== null) return previewShiftMs;

    previewShiftMs = 0;
    try {
      if (new URLSearchParams(window.location.search).has('sale_countdown_preview')) {
        var now = Date.now();
        if (now < start) previewShiftMs = start - now;
      }
    } catch (e) {
      previewShiftMs = 0;
    }
    return previewShiftMs;
  }

  /*
   * Een gedeelde ticker voor alle timers op de pagina. Met een eigen interval
   * per element lopen de tikmomenten enkele tientallen milliseconden uiteen en
   * kunnen twee timers kort een verschillende seconde tonen. Nu rendert elke
   * timer in dezelfde tik, dus ze zijn ook op het scherm altijd gelijk.
   */
  var instances = new Set();
  var ticker = null;

  function startTicker() {
    if (ticker) return;
    ticker = setInterval(function () {
      instances.forEach(function (el) { el.tick(); });
    }, 1000);
  }

  function stopTickerIfIdle() {
    if (ticker && instances.size === 0) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;

        this.start = parseInt(this.getAttribute('data-start'), 10);
        this.end = parseInt(this.getAttribute('data-end'), 10);
        this.cycle = parseInt(this.getAttribute('data-cycle'), 10) || 86400000;
        this.dailyReset = this.getAttribute('data-daily-reset') === 'true';
        this.compact = this.getAttribute('data-compact') === 'true';
        this.unitsEl = this.querySelector('[data-sale-countdown-units]');
        this.labels = this.readLabels();

        if (!this.unitsEl || isNaN(this.start) || isNaN(this.end) || this.cycle <= 0) {
          this.remove();
          return;
        }

        var shift = previewShift(this.start);
        this.start -= shift;
        this.end -= shift;
      }

      /*
       * Restart on reconnect too. The announcement bar moves its items around,
       * which produces a disconnect followed by a connect; without this the
       * timer would freeze on its first reading while the others kept counting.
       */
      this.run();
    }

    run() {
      if (!this.unitsEl || instances.has(this)) return;
      instances.add(this);
      this.tick();
      startTicker();
    }

    disconnectedCallback() {
      instances.delete(this);
      stopTickerIfIdle();
    }

    readLabels() {
      var out = {};
      var given = {};
      try {
        given = JSON.parse(this.getAttribute('data-labels') || '{}');
      } catch (e) {
        given = {};
      }
      for (var key in FALLBACK_LABELS) {
        out[key] = given[key] || FALLBACK_LABELS[key];
      }
      return out;
    }

    /* The moment being counted down to. Outside the window: null. */
    target(now) {
      if (now < this.start || now >= this.end) return null;
      if (!this.dailyReset) return this.end;

      var elapsed = now - this.start;
      var next = this.start + (Math.floor(elapsed / this.cycle) + 1) * this.cycle;
      return Math.min(next, this.end);
    }

    tick() {
      var now = Date.now();
      var target = this.target(now);

      if (target === null) {
        this.stop();
        return;
      }

      this.render(target - now);
      this.hidden = false;
    }

    render(remaining) {
      var total = Math.max(remaining, 0);
      var days = Math.floor(total / 86400000);
      var hours = Math.floor((total % 86400000) / 3600000);
      var mins = Math.floor((total % 3600000) / 60000);
      var secs = Math.floor((total % 60000) / 1000);
      var l = this.labels;
      var html = '';

      if (this.compact) {
        if (days > 0) html += '<span class="sale-countdown__unit">' + days + l.d + '</span>';
        html +=
          '<span class="sale-countdown__unit">' + pad(hours) + l.h + '</span>' +
          '<span class="sale-countdown__unit">' + pad(mins) + l.m + '</span>' +
          '<span class="sale-countdown__unit">' + pad(secs) + l.s + '</span>';
      } else {
        if (days > 0) {
          html += this.unit(days, days === 1 ? l.day : l.days);
        }
        html +=
          this.unit(pad(hours), hours === 1 ? l.hour : l.hours) +
          this.unit(pad(mins), l.minutes) +
          this.unit(pad(secs), l.seconds);
      }

      this.unitsEl.innerHTML = html;
    }

    unit(value, label) {
      return (
        '<span class="sale-countdown__unit">' +
        '<span class="sale-countdown__value">' + value + '</span>' +
        '<span class="sale-countdown__label">' + label + '</span>' +
        '</span>'
      );
    }

    stop() {
      instances.delete(this);
      this.hidden = true;
      stopTickerIfIdle();
    }
  }

  if (!customElements.get('sale-countdown')) {
    customElements.define('sale-countdown', SaleCountdown);
  }
})();
